/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import ILogoModule from "./scene_logo_interface";
import {StorageUtils, RenderDocument} from "js_renderer";
import {vec2} from "gl-matrix";
import TexturedQuad from "../../elements/hud_elements/textured_quad";
import Scene from "../../scene";
import {ELogoScaleType, ELogoType} from "./scene_logo_enums";
import Settings from "../settings";
import IBackgroundModule from "../background/scene_background_interface";

/***
 * @param aLeftOffset 0..1 left corner of the image
 * @param aTopOffset 0..1 top offset of he image, starting from bottom so 0 is down, 1 is up
 * @param aWidth when 0, the size will be determined from the image
 * @param aHeight when 0, the size will be determined from the image
 */
export interface ILogoSettings {
    topOffset?: number;
    width?: number;
    leftOffset?: number;
    height?: number;
    customLogoImageNameLight?: string;
    customLogoImageNameDark?: string;
    scaleType?: ELogoScaleType;
    originalAspectRatio?: number;
}

export default // @ts-ignore
class LogoModule extends AbstractModule implements ILogoModule {

    protected static _typeName: string = "logo";
    protected _defaultSettings: ILogoSettings;
    protected _settings: ILogoSettings;

    private _logos: {[key: number]: any};
    private readonly _limits = [500 / window.devicePixelRatio, 900 / window.devicePixelRatio];
    private readonly _minScale = 0.20;
    private _defaultScale = 1.0;

    private _enabled: boolean;
    private _activeLogo: number;
    private _scaleX: number;
    private _scaleY: number;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        let view = this.sceneAPI.viewModeModule.getActiveView();
        if (view) {
            view.registerViewResizedObserver(this);
        }

        this._type = LogoModule._typeName;

        this._logos = {};
        this._enabled = true;
        this._activeLogo = ELogoType.LIGHT;
        this._scaleX =  1.0;
        this._scaleY = 1.0;

        this._defaultSettings = {
            width: 0, // when 0, the the original texture will be used
            height: 0, // when 0, the the original texture will be used
            leftOffset: 1,
            topOffset: 0,
            customLogoImageNameLight: "",
            customLogoImageNameDark: "",
            scaleType: ELogoScaleType.NORMAL,
            originalAspectRatio: 1
        };

        this._settings = Settings.clone(this._defaultSettings);
    }

    public static getClassType(): string {
        return LogoModule._typeName;
    }

    public addLogo(aImageFullUrl: string, aType: number) {
        if (this._logos[aType] !== undefined && this._logos[aType].sapiElement) {
            this._logos[aType].sapiElement.remove();
        }

        let sapiElement = this.sceneDocument.createAPIElement(TexturedQuad.TypeName) as TexturedQuad;
        this.sceneDocument.attachElementToInternalModel(sapiElement);
        sapiElement.setTexture(aImageFullUrl, [0, 0], true, [0, 1], [1, -1]);
        sapiElement.extendProperties({onHud: true, zIndex: 0, pickable: false, clippable: false, transparency: 0.2});

        // we load the logo here in advance ust to get the information about its width and height
        let image = new Image();
        // @ts-ignore
        image.loaded = false;
        // @ts-ignore
        image.type = "" + aType;

        image.onload = () => {
            // @ts-ignore
            image.loaded = true;
            let view = this.sceneAPI.viewModeModule.getActiveView();
            this.viewResized(view.size);

        };

        this._logos[aType] = {
            url: aImageFullUrl,
            sapiElement: sapiElement,
            image: image
        };

        // look inside DOM for the logo, TODO: return correct extension jpg/png from inside the function
        let inlinedImageUrl = StorageUtils.getDOMInlinedImage(aImageFullUrl);
        if (inlinedImageUrl) {
            aImageFullUrl = inlinedImageUrl;
        }
        image.src = aImageFullUrl;
    }

    public getOrigialAspectRatio(): number {
        return this._settings.originalAspectRatio;
    }

    public getLogoScaleType(): ELogoScaleType {
        return this._settings.scaleType;
    }

    public getCustomLogoImageNameLight(): string {
        return this._settings.customLogoImageNameLight;
    }

    public getCustomLogoImageLightUrl(): string {
        return this.sceneDocument.getMainModel().getBaseUrl() + this.getCustomLogoImageNameLight();
    }

    public getCustomLogoImageNameDark(): string {
        return this._settings.customLogoImageNameDark;
    }

    public getCustomLogoImageDarkUrl(): string {
        return this.sceneDocument.getMainModel().getBaseUrl() + this.getCustomLogoImageNameDark();
    }

    public getActiveLogo(): ELogoType {
        return this._activeLogo;
    }

    public setEnabled(aValue: boolean) {

        for (let key in this._logos) {
            let logo = this._logos[key];
            if (logo.image.loaded && logo.sapiElement) {
                if (("" + this._activeLogo) === key) {
                    logo.sapiElement.setVisibility(aValue ? true : false);
                }
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public isEnabled(): boolean {
        return this._enabled;
    }

    /***
     * Callback called when the view is resized
     *
     * @param aContentSize pair of [width, height]
     */
    public viewResized(aContentSize: vec2) {

        this._resizeLogo(aContentSize);

        this._updateSapiElements(aContentSize);

        for (let key in this._logos) {
            let logo = this._logos[key];
            if (logo.image.loaded) {
                logo.sapiElement.draw(); // must be drawn to create a render element
                if (("" + this._activeLogo) === key) {
                    logo.sapiElement.setVisibility(true);
                } else {
                    logo.sapiElement.setVisibility(false);
                }
            }
        }
    }

    public viewChanged() {
        //
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: ILogoSettings = {};

        // backward compatibility
        if (aJsonObject.hasOwnProperty("ModelConfiguration")) {
            let modelConfig: any = aJsonObject.ModelConfiguration;

            if (modelConfig.hasOwnProperty("customLogoImageNameLight")) {
                settings.customLogoImageNameLight = modelConfig.customLogoImageNameLight;
            }

            if (modelConfig.hasOwnProperty("customLogoImageNameDark")) {
                settings.customLogoImageNameDark = modelConfig.customLogoImageNameDark;
            }

            if (modelConfig.hasOwnProperty("logoScaleType")) {
                settings.scaleType = modelConfig.logoScaleType;
            }

            if (modelConfig.hasOwnProperty("logoOriginalAspectRatio")) {
                settings.originalAspectRatio = modelConfig.logoOriginalAspectRatio;
            }
        }
        // end backward compatibility

        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }

        this.set(settings);
    }

    /***
     * Force scale of logo for one render pass, which is needed for finish printing in resolution which is bigger than current canvas size
     * @param aContentSize
     * @param aForcedScale
     */
    public forceTemporaryScale(aContentSize: vec2, aForcedScale: vec2) {
        let size = vec2.clone(aContentSize);

        // console.log(size);
        size[0] = size[0] / window.devicePixelRatio;
        size[1] = size[1] / window.devicePixelRatio;

        let scale = this._defaultScale;
        if (size[0] < this._limits[0]) {
            // this._enabled = false; // TB: this should stay enabled, but logo shall not be rendered
            this._scaleX = 0;
            this._scaleY = 0;
        }
        else {
            if (size[0] < this._limits[1]) {
                let scaleInterval = this._defaultScale - this._minScale;
                let s = size[0] / this._limits[1];
                scale = this._minScale + s * scaleInterval;

                // scale = scale * window.devicePixelRatio;
                this._scaleX = scale;
                this._scaleY = scale;
            }
            else {
                this._scaleX = this._defaultScale * aForcedScale[0];
                this._scaleY = this._defaultScale * aForcedScale[1];
            }
        }

        this._updateSapiElements(null);
    }

    protected setActiveLogo(aType: ELogoType) {

        for (let key in this._logos) {
            let logo = this._logos[key];
            if (key === ("" + aType)) { // not === because we compare string  (key) and value (number)
                logo.sapiElement.setVisibility(true);
            } else {
                logo.sapiElement.setVisibility(false);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings.hasOwnProperty("width") || aChangedSettings.hasOwnProperty("height") ||
            aChangedSettings.hasOwnProperty("leftOffset") || aChangedSettings.hasOwnProperty("topOffset")) {
            this._updateSapiElements(null);
        }

        switch (this.getLogoScaleType()) {
            case 0 :
                this._defaultScale = 0.5;
                break;
            case 1 :
                this._defaultScale = 1.0;
                break;
            case 2 :
                this._defaultScale = 2;
                break;
        }
    }

    protected notifySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings.hasOwnProperty("background")) {
            let bgModule = aChangedSettings['background'];
            if (bgModule.hasOwnProperty("background")) {
                let bg = bgModule['background'];
                if (bg.hasOwnProperty('type') || bg.hasOwnProperty('color')) {
                    this._activeLogo =
                        (this.sceneAPI.getModuleByType('background') as IBackgroundModule).isBackgroundColorLight() ? ELogoType.DARK : ELogoType.LIGHT;
                    this.setActiveLogo(this._activeLogo);
                }
            }
        }
    }

    protected _resizeLogo(aContentSize: vec2) {
        let size = vec2.clone(aContentSize);

        // console.log(size);
        size[0] = size[0] / window.devicePixelRatio;
        size[1] = size[1] / window.devicePixelRatio;

        let scale = this._defaultScale;
        if (size[0] < this._limits[0]) {
            // this._enabled = false; // TB: this should stay enabled, but logo shall not be rendered
            this._scaleX = 0;
            this._scaleY = 0;
        }
        else {
            if (size[0] < this._limits[1]) {
                let scaleInterval = this._defaultScale - this._minScale;
                let s = size[0] / this._limits[1];
                scale = this._minScale + s * scaleInterval;
            }
            // scale = scale * window.devicePixelRatio;
            this._scaleX = scale;
            this._scaleY = scale;
        }
        // console.log(scale + ' / ' + size[0] + ', ' + size[1] + ' : ' + window.devicePixelRatio);
    }

    private _updateSapiElements(aContentSize: vec2) {
        let viewSize;
        if (!aContentSize) {
            let view = this.sceneAPI.viewModeModule.getActiveView();
            if (!view) {
                return;
            }
            viewSize = view.size;
        } else {
            viewSize = aContentSize;
        }

        for (let key in this._logos) {
            let logo = this._logos[key];
            if (logo.sapiElement && logo.image.loaded) {

                // values from 0 to 1
                let widthPx;
                let heightPx;
                if (this._settings.width) {
                    widthPx = this._settings.width;
                    heightPx = this._settings.height;
                } else {
                    widthPx = logo.image.width;
                    heightPx = logo.image.height;
                }

                // TODO: is the computations here correct for all cases, I bet not :(
                // origin[0,0] of canvas is in left bottom corner
                logo.sapiElement.setPositionAndSize(
                    this._settings.leftOffset + ((-(widthPx * this._scaleX)) / viewSize[0]),
                    this._settings.topOffset + (0 / viewSize[1]),
                    (widthPx * this._scaleX) / viewSize[0],
                    (heightPx * this._scaleY) / viewSize[1]
                );
            }
        }
    }

}

Scene.registerSceneAPIModule(LogoModule);
